import axios, { AxiosResponse } from "axios";
import querystring from "querystring";
import { z } from "zod";

import { AlertColor } from "@mui/material/Alert/Alert";

import { getAdjustedTimesFormatted } from "Common/dateUtilities";

import { IResultPages } from "Types/EVaultAppTypes";

import { MersDatabaseTransactionType } from "../VaultInterfaces";
import { IMinReconciliationReport } from "./MinReconciliationReportInterfaces";
import { IMinReconciliationReportsSearchApiParams } from "./MinReconciliationSlice";

const reportRoute = "/api/v1/mers/reconciliation-reports";

export interface IMinReconciliationReportsPagedData {
  links: IResultPages;
  results: IMinReconciliationReport[];
}

export function getMinReconciliationReportsByUrl(url: string): Promise<IMinReconciliationReportsPagedData> {
  return axios.get(url).then((response) => response.data);
}

interface IMinReconciliationReportsSearchPayload {
  searchTerm?: string;
  start?: string;
  stop?: string;
  limit: number;
  offset: number;
}

function getMinReconciliationReportsQueryString(params: IMinReconciliationReportsSearchApiParams) {
  const payload: IMinReconciliationReportsSearchPayload = {
    ...getAdjustedTimesFormatted(params.start, params.stop, true),
    limit: params.limit,
    offset: params.offset,
    searchTerm: params.searchTerm,
  };

  return "?" + querystring.stringify(payload as any);
}

export function getMinReconciliationReportsUrl(params: IMinReconciliationReportsSearchApiParams): string {
  return `${reportRoute}${getMinReconciliationReportsQueryString(params)}`;
}

/**
 * This interface represents the group status results within a reconciliation report.
 */
export interface IMersMinReconciliationReportStatus {
  count: number;
  transactionStatuses: Record<MersDatabaseTransactionType, { count: number }>;
}

export enum MinReconciliationReportDetailsType {
  all = 0,
  noteInServicerNotInMers = 1,
  noteInServicerInMersOrgMismatch = 2,
  noteInServicerInMersOrgMatch = 3,
  minInMersNotInServicerSowClient = 4,
  minInMersNotInServicerNotSowClient = 5,
  minInMersNoNotIServicer = 6,
  statusDoesNotMatch = 7,
  controllerOrgIdInServicerDoesNotMatch = 8,
  statusMatches = 9,
  error = 10,
}

export type MinReconciliationReportDetailsTypeKeys = keyof typeof MinReconciliationReportDetailsType;

export interface IMinReconciliationReportStatusDetailsResponse {
  count: number;
  description: string;
  detailedDescription: string;
  severity: AlertColor | undefined;
  type: MinReconciliationReportDetailsTypeKeys;
}

/**
 * This interface represents the data that will be returned in reconciliation report status requests.
 */
export interface IMinReconciliationReportStatusResponse {
  count: number;
  details: IMinReconciliationReportStatusDetailsResponse[];
}

/**
 * Requests the status summary for the requested reconciliation report.
 */
export async function getMinReconciliationReportStatus(
  reportId: string
): Promise<AxiosResponse<IMinReconciliationReportStatusResponse>> {
  const response = await axios.get<IMinReconciliationReportStatusResponse>(`${reportRoute}/${reportId}/status`);
  if (response.status < 400) {
    response.data.details = response.data.details.map((details) => {
      let severity: AlertColor | undefined = details.severity?.toLowerCase() as AlertColor;
      const validAlertColors: AlertColor[] = ["error", "warning", "info", "success"];

      if (!validAlertColors.includes(severity)) {
        severity = undefined;
        console.error(`Invalid severity found ${details.severity}`);
      }
      return {
        ...details,
        severity,
      };
    });
  }
  return response;
}

export const mersOrgSchema = z.object({
  clientCode: z.string().length(4),
  mersOrgId: z.string().length(7).regex(new RegExp("^[0-9]*$"), "Only numeric characters are allowed"),
  name: z.string().min(1).max(100),
});

export type ReconciliationCreateClientMersOrg = z.infer<typeof mersOrgSchema>;

export type IReconciliationClientMersOrg = ReconciliationCreateClientMersOrg & {
  id: string;
};

const baseClientMersOrgUrl = "/api/v1/min-reconciliation-mersorgs";

function getClientClientBaseUrl(clientCode: string) {
  return `/api/v1/clients/${clientCode}/min-reconciliation-mersorgs`;
}

/**
 * Gets the client MERS orgs for MIN reconciliation.
 */
export async function getReconciliationClientMersOrgs() {
  return await axios.get<IReconciliationClientMersOrg[]>(baseClientMersOrgUrl);
}

/**
 * Adds the client mersOrg to the local list.
 */
export async function addClientMersOrg(clientCode: string, mersOrg: ReconciliationCreateClientMersOrg) {
  return axios.post(getClientClientBaseUrl(clientCode), mersOrg);
}

/**
 * Deletes the client mers orgs from the local list.
 */
export async function deleteClientMersOrg(clientMersOrgId: string, clientCode: string) {
  return axios.delete(`${getClientClientBaseUrl(clientCode)}/${clientMersOrgId}`);
}

/**
 * Updates the client mers orgs in the local list.
 */
export async function updateClientMersOrg(
  clientMersOrgId: string,
  mersOrgUpdate: ReconciliationCreateClientMersOrg,
  clientCode: string
) {
  return axios.put(`${getClientClientBaseUrl(clientCode)}/${clientMersOrgId}`, mersOrgUpdate);
}
